// src/pages/ConsoleModels.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { FaTools, FaSearch } from 'react-icons/fa';
import RepairsModal from '../components/RepairsModal';

function ConsoleModels() {
    const { brand: brandUrlParam } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [models, setModels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [selectedConsole, setSelectedConsole] = useState(null);
    const [repairsData, setRepairsData] = useState([]);
    const [showRepairsModal, setShowRepairsModal] = useState(false);
    const [isLoadingRepairs, setIsLoadingRepairs] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    const [hasOpenedModalFromUrl, setHasOpenedModalFromUrl] = useState(false);

    const getConsoleDbBrand = useCallback((urlBrand) => {
        const lower = urlBrand?.toLowerCase();
        switch (lower) {
            case 'xbox': return 'Xbox';
            case 'playstation': return 'PlayStation';
            case 'nintendo': return 'Nintendo';
            default: return urlBrand;
        }
    }, []);

    // Function to extract or determine console release year
    const getConsoleReleaseYear = useCallback((consoleName, brand) => {
        const name = consoleName.toLowerCase();
        
        // Console model to year mapping for known models
        const consoleYearMapping = {
            // PlayStation models
            'playstation 5': 2020,
            'ps5': 2020,
            'playstation 5 digital': 2020,
            'ps5 digital': 2020,
            'playstation 4 pro': 2016,
            'ps4 pro': 2016,
            'playstation 4 slim': 2016,
            'ps4 slim': 2016,
            'playstation 4': 2013,
            'ps4': 2013,
            'playstation 3 super slim': 2012,
            'ps3 super slim': 2012,
            'playstation 3 slim': 2009,
            'ps3 slim': 2009,
            'playstation 3': 2006,
            'ps3': 2006,
            'playstation 2 slim': 2004,
            'ps2 slim': 2004,
            'playstation 2': 2000,
            'ps2': 2000,
            'playstation': 1994,
            'ps1': 1994,
            'psx': 1994,
            
            // Xbox models
            'xbox series x': 2020,
            'xbox series s': 2020,
            'xbox series': 2020,
            'xbox one x': 2017,
            'xbox one s': 2016,
            'xbox one': 2013,
            'xbox 360 s': 2010,
            'xbox 360 e': 2013,
            'xbox 360': 2005,
            'xbox': 2001,
            
            // Nintendo models
            'nintendo switch oled': 2021,
            'switch oled': 2021,
            'nintendo switch': 2017,
            'switch': 2017,
            'nintendo switch lite': 2019,
            'switch lite': 2019,
            'nintendo wii u': 2012,
            'wii u': 2012,
            'nintendo wii': 2006,
            'wii': 2006,
            'nintendo gamecube': 2001,
            'gamecube': 2001,
            'nintendo 64': 1996,
            'n64': 1996,
            'super nintendo': 1990,
            'snes': 1990,
            'nintendo entertainment system': 1985,
            'nes': 1985,
            
            // Handheld consoles
            'nintendo 3ds xl': 2012,
            '3ds xl': 2012,
            'nintendo 3ds': 2011,
            '3ds': 2011,
            'nintendo 2ds': 2013,
            '2ds': 2013,
            'nintendo ds lite': 2006,
            'ds lite': 2006,
            'nintendo ds': 2004,
            'ds': 2004,
            'playstation vita': 2011,
            'ps vita': 2011,
            'psp go': 2009,
            'playstation portable': 2004,
            'psp': 2004,
        };
        
        // Check exact match first
        const exactMatch = consoleYearMapping[name];
        if (exactMatch) return exactMatch;
        
        // Try partial matching for variations
        for (const [modelName, year] of Object.entries(consoleYearMapping)) {
            if (name.includes(modelName) || modelName.includes(name)) {
                return year;
            }
        }
        
        // Extract year from console name (look for 4-digit years)
        const yearMatch = name.match(/\b(19[0-9]{2}|20[0-9]{2})\b/);
        if (yearMatch) {
            return parseInt(yearMatch[1]);
        }
        
        // Default to 2015 if year cannot be determined
        return 2015;
    }, []);

    // Function to sort consoles by year (latest first)
    const sortConsolesByYear = useCallback((consoles) => {
        return [...consoles].sort((a, b) => {
            const yearA = getConsoleReleaseYear(a.model || a.name, a.brand);
            const yearB = getConsoleReleaseYear(b.model || b.name, b.brand);
            
            // Sort by year descending (latest first)
            if (yearB !== yearA) {
                return yearB - yearA;
            }
            
            // If same year, sort by name
            const nameA = (a.model || a.name).toLowerCase();
            const nameB = (b.model || b.name).toLowerCase();
            
            // For same year, prioritize main console over variants
            const getPriority = (name) => {
                if (name.includes(' x')) return 4;
                if (name.includes(' s')) return 3;
                if (name.includes(' pro')) return 4;
                if (name.includes(' slim')) return 2;
                if (name.includes(' lite')) return 2;
                if (name.includes(' oled')) return 3;
                return 1;
            };
            
            const priorityA = getPriority(nameA);
            const priorityB = getPriority(nameB);
            
            if (priorityB !== priorityA) {
                return priorityB - priorityA;
            }
            
            return nameA.localeCompare(nameB);
        });
    }, [getConsoleReleaseYear]);

    // Fetch consoles from API based on brand
    useEffect(() => {
        const fetchConsoles = async () => {
            setLoading(true);
            setError(null);
            try {
                const dbBrand = getConsoleDbBrand(brandUrlParam);
                const response = await fetch('/api/consoles');
                
                if (!response.ok) {
                    throw new Error(`Failed to fetch consoles: ${response.status}`);
                }
                
                const data = await response.json();
                
                // Filter consoles by brand and map to expected structure
                const brandConsoles = data
                    .filter(console => console.brand.toLowerCase() === dbBrand.toLowerCase())
                    .map(console => ({
                        name: console.model,
                        model: console.model,
                        brand: console.brand,
                        image: console.image || '',
                        repairs: console.repairs || []
                    }));
                
                // Sort consoles by year (latest first)
                const sortedConsoles = sortConsolesByYear(brandConsoles);
                setModels(sortedConsoles);
            } catch (err) {
                console.error('Error fetching consoles:', err);
                setError(err.message || 'Failed to load consoles');
                setModels([]);
            } finally {
                setLoading(false);
            }
        };

        if (brandUrlParam) {
            fetchConsoles();
        }
    }, [brandUrlParam, getConsoleDbBrand, sortConsolesByYear]);

    // Filter models based on search term
    const filteredModels = useMemo(() => {
        if (!searchTerm.trim()) return models;
        
        const searchLower = searchTerm.toLowerCase();
        const filteredModels = models.filter(model => 
            model.name.toLowerCase().includes(searchLower)
        );
        
        // Re-sort filtered results to maintain latest-to-oldest order
        return sortConsolesByYear(filteredModels);
    }, [models, searchTerm, sortConsolesByYear]);

    // Group consoles by generation/type
    const groupedModels = useMemo(() => {
        if (brandUrlParam?.toLowerCase() === 'playstation') {
            const groups = {
                'PlayStation 5': [],
                'PlayStation 4': [],
                'PlayStation 3': [],
                'PSP & PS Vita': [],
                'Others': []
            };
            
            const modelsToGroup = searchTerm.trim() 
                ? models.filter(model => model.name.toLowerCase().includes(searchTerm.toLowerCase()))
                : models;
                
            modelsToGroup.forEach(model => {
                const lowerName = model.name.toLowerCase();
                if (lowerName.includes('playstation 5') || lowerName.includes('ps5')) {
                    groups['PlayStation 5'].push(model);
                } else if (lowerName.includes('playstation 4') || lowerName.includes('ps4')) {
                    groups['PlayStation 4'].push(model);
                } else if (lowerName.includes('playstation 3') || lowerName.includes('ps3')) {
                    groups['PlayStation 3'].push(model);
                } else if (lowerName.includes('psp') || lowerName.includes('vita')) {
                    groups['PSP & PS Vita'].push(model);
                } else {
                    groups['Others'].push(model);
                }
            });
            
            // Sort each group from latest to oldest
            Object.keys(groups).forEach(groupName => {
                groups[groupName] = sortConsolesByYear(groups[groupName]);
            });
            
            return groups;
        } else if (brandUrlParam?.toLowerCase() === 'xbox') {
            const groups = {
                'Xbox Series X/S': [],
                'Xbox One': [],
                'Xbox 360': [],
                'Others': []
            };
            
            const modelsToGroup = searchTerm.trim() 
                ? models.filter(model => model.name.toLowerCase().includes(searchTerm.toLowerCase()))
                : models;
                
            modelsToGroup.forEach(model => {
                const lowerName = model.name.toLowerCase();
                if (lowerName.includes('series')) {
                    groups['Xbox Series X/S'].push(model);
                } else if (lowerName.includes('xbox one')) {
                    groups['Xbox One'].push(model);
                } else if (lowerName.includes('360')) {
                    groups['Xbox 360'].push(model);
                } else {
                    groups['Others'].push(model);
                }
            });
            
            // Sort each group from latest to oldest
            Object.keys(groups).forEach(groupName => {
                groups[groupName] = sortConsolesByYear(groups[groupName]);
            });
            
            return groups;
        }
        return null;
    }, [brandUrlParam, models, searchTerm, sortConsolesByYear]);

    useEffect(() => {
        setSelectedConsole(null);
        setShowRepairsModal(false);
        setIsLoadingRepairs(false);
        setFetchError(null);
        setRepairsData([]);
        setSearchTerm('');
    }, [brandUrlParam]);

    const handleModelClick = useCallback(async (model) => {
        if (!model || isLoadingRepairs) return;
        setSelectedConsole(model);
        setIsLoadingRepairs(true);
        setFetchError(null);
        setRepairsData([]);
        setShowRepairsModal(true);
        
        try {
            // Use the repairs data that came with the console
            if (model.repairs && model.repairs.length > 0) {
                setRepairsData(model.repairs);
            } else {
                // Fallback to API call if repairs not included
                const dbBrand = model.brand || getConsoleDbBrand(brandUrlParam);
                const url = `/api/consoles/repairs?brand=${dbBrand}&model=${encodeURIComponent(model.name)}`;
                console.log("Request URL:", url);
                const response = await fetch(url);
                if (!response.ok) throw new Error(`Server error: ${response.status} ${response.statusText}`);
                const data = await response.json();
                setRepairsData(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error('Error fetching console repairs data:', error);
            setFetchError(error.message || 'Failed to load repair details.');
            setRepairsData([]);
        } finally {
            setIsLoadingRepairs(false);
        }
    }, [isLoadingRepairs, brandUrlParam, getConsoleDbBrand]);

    const handleModelKeyDown = useCallback((event, model) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleModelClick(model);
        }
    }, [handleModelClick]);

    const handleCloseModal = useCallback(() => {
        setShowRepairsModal(false);
    }, []);

    // Check for model query parameter and auto-open modal
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const modelParam = params.get('model');
        
        if (modelParam && models.length > 0 && !isLoadingRepairs && !hasOpenedModalFromUrl) {
            setHasOpenedModalFromUrl(true);
            
            // Clear the query parameter immediately
            const newParams = new URLSearchParams(location.search);
            newParams.delete('model');
            const newSearch = newParams.toString();
            const newUrl = location.pathname + (newSearch ? `?${newSearch}` : '');
            window.history.replaceState({}, '', newUrl);
            
            // Normalize the search query
            const normalizedParam = modelParam.toLowerCase().trim();
            
            // Find the matching model - EXACT MATCH ONLY
            const matchingModel = models.find(model => {
                const modelName = (model.name || model.model || '').toLowerCase().trim();
                
                // EXACT MATCH ONLY - case insensitive
                if (modelName === normalizedParam) {
                    console.log('✓ Exact match found:', modelName);
                    return true;
                }
                
                return false;
            });
            
            if (matchingModel) {
                console.log('Opening modal for console:', matchingModel.name);
                setTimeout(() => {
                    handleModelClick(matchingModel);
                }, 100);
            } else {
                // Show "Device not found" message
                console.log('❌ No exact match found for:', normalizedParam);
                
                // Show error notification
                const notification = document.createElement('div');
                notification.style.cssText = `
                    position: fixed;
                    top: 20px;
                    left: 50%;
                    transform: translateX(-50%);
                    background: #dc3545;
                    color: white;
                    padding: 15px 25px;
                    border-radius: 8px;
                    z-index: 10000;
                    font-size: 14px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                    animation: slideDown 0.3s ease;
                `;
                notification.innerHTML = `
                    <strong>❌ Device not found</strong><br>
                    "${modelParam}" is not available
                `;
                document.body.appendChild(notification);
                
                // Add animation
                const style = document.createElement('style');
                style.textContent = `
                    @keyframes slideDown {
                        from { transform: translate(-50%, -100%); opacity: 0; }
                        to { transform: translate(-50%, 0); opacity: 1; }
                    }
                `;
                document.head.appendChild(style);
                
                // Remove notification after 5 seconds
                setTimeout(() => {
                    notification.style.animation = 'slideDown 0.3s ease reverse';
                    setTimeout(() => {
                        document.body.removeChild(notification);
                        document.head.removeChild(style);
                    }, 300);
                }, 5000);
            }
        }
    }, [models, location.search, handleModelClick, isLoadingRepairs, hasOpenedModalFromUrl]);

    const getBrandDisplayName = (brand) => {
        const brandMap = {
            'xbox': 'Xbox',
            'playstation': 'PlayStation',
            'nintendo': 'Nintendo'
        };
        return brandMap[brand?.toLowerCase()] || brand;
    };

    const brandDisplayName = getBrandDisplayName(brandUrlParam);

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <div style={styles.spinner}></div>
                <p>Loading {brandDisplayName} models...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div style={styles.errorContainer}>
                <p style={styles.errorText}>Error: {error}</p>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>Select Your {brandDisplayName} Model</h1>
            </div>

            {/* Search Bar */}
            <div style={styles.searchContainer}>
                <FaSearch style={styles.searchIcon} />
                <input
                    type="text"
                    placeholder={`Search ${brandDisplayName} models...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={styles.searchInput}
                />
            </div>

            {/* Results count */}
            {searchTerm && (
                <p style={styles.resultsCount}>
                    Found {filteredModels.length} model{filteredModels.length !== 1 ? 's' : ''}
                </p>
            )}

            {/* Models Grid */}
            {groupedModels ? (
                // Grouped display for PlayStation and Xbox
                <div style={styles.groupedContainer}>
                    {Object.entries(groupedModels).map(([groupName, groupModels]) => {
                        if (groupModels.length === 0) return null;
                        return (
                            <div key={groupName} style={styles.groupSection}>
                                <h2 style={styles.groupTitle}>{groupName}</h2>
                                <div style={styles.modelsGrid}>
                                    {groupModels.map((model, index) => (
                                        <div
                                            key={index}
                                            style={styles.modelCard}
                                            onClick={() => handleModelClick(model)}
                                            onKeyDown={(e) => handleModelKeyDown(e, model)}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-5px)';
                                                e.currentTarget.style.boxShadow = '0 5px 15px rgba(0,0,0,0.3)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = 'none';
                                            }}
                                            tabIndex={0}
                                            role="button"
                                            aria-label={`Select ${model.name} for repairs`}
                                        >
                                            <img
                                                src={model.image || '/images/console-placeholder.png'}
                                                alt={model.name}
                                                style={styles.modelImage}
                                                onError={(e) => {
                                                    e.target.src = '/images/console-placeholder.png';
                                                }}
                                            />
                                            <p style={styles.modelName}>{model.name}</p>
                                            <div style={styles.repairIcon}>
                                                <FaTools />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                // Regular grid for other brands
                <div style={styles.modelsGrid}>
                    {filteredModels.length > 0 ? (
                        filteredModels.map((model, index) => (
                            <div
                                key={index}
                                style={styles.modelCard}
                                onClick={() => handleModelClick(model)}
                                onKeyDown={(e) => handleModelKeyDown(e, model)}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-5px)';
                                    e.currentTarget.style.boxShadow = '0 5px 15px rgba(0,0,0,0.3)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                                tabIndex={0}
                                role="button"
                                aria-label={`Select ${model.name} for repairs`}
                            >
                                <img
                                    src={model.image || '/images/console-placeholder.png'}
                                    alt={model.name}
                                    style={styles.modelImage}
                                    onError={(e) => {
                                        e.target.src = '/images/console-placeholder.png';
                                    }}
                                />
                                <p style={styles.modelName}>{model.name}</p>
                                <div style={styles.repairIcon}>
                                    <FaTools />
                                </div>
                            </div>
                        ))
                    ) : (
                        <p style={styles.noModelsText}>
                            {searchTerm 
                                ? `No ${brandDisplayName} models found matching "${searchTerm}"`
                                : `No ${brandDisplayName} models available`}
                        </p>
                    )}
                </div>
            )}

            {/* Repairs Modal */}
            {showRepairsModal && (
                <RepairsModal
                    show={showRepairsModal}
                    onClose={handleCloseModal}
                    watchModel={{
                        name: selectedConsole?.name || 'Device',
                        repairs: repairsData,
                        image: selectedConsole?.image
                    }}
                />
            )}
        </div>
    );
}

// Styles - Black and White Theme
const styles = {
    container: {
        padding: '20px',
        maxWidth: '1200px',
        margin: '0 auto',
        minHeight: '100vh',
        backgroundColor: '#f8f9fa'
    },
    header: {
        marginBottom: '30px',
        textAlign: 'center'
    },
    backButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        color: '#6c757d',
        textDecoration: 'none',
        fontSize: '16px',
        marginBottom: '20px',
        transition: 'opacity 0.3s',
        '&:hover': {
            opacity: '0.7'
        }
    },
    title: {
        fontSize: '2.5em',
        fontWeight: 'bold',
        color: '#6c757d',
        margin: '0'
    },
    searchContainer: {
        position: 'relative',
        maxWidth: '500px',
        margin: '0 auto 30px',
    },
    searchIcon: {
        position: 'absolute',
        left: '15px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#6c757d',
        fontSize: '18px'
    },
    searchInput: {
        width: '100%',
        padding: '12px 20px 12px 45px',
        fontSize: '16px',
        border: '2px solid #6c757d',
        borderRadius: '25px',
        outline: 'none',
        transition: 'border-color 0.3s',
        backgroundColor: '#fff',
        color: '#6c757d'
    },
    resultsCount: {
        textAlign: 'center',
        color: '#6c757d',
        marginBottom: '20px',
        fontSize: '14px'
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        gap: '20px'
    },
    spinner: {
        width: '50px',
        height: '50px',
        border: '3px solid #ddd',
        borderTop: '3px solid #6c757d',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
    },
    errorContainer: {
        textAlign: 'center',
        padding: '40px',
        minHeight: '400px'
    },
    errorText: {
        color: '#6c757d',
        fontSize: '18px',
        marginBottom: '20px'
    },
    backLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        color: '#6c757d',
        textDecoration: 'none',
        fontSize: '16px',
        '&:hover': {
            opacity: '0.7'
        }
    },
    groupedContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '40px'
    },
    groupSection: {
        backgroundColor: '#fff',
        borderRadius: '10px',
        padding: '20px',
        border: '2px solid #6c757d',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
    },
    groupTitle: {
        fontSize: '1.8em',
        fontWeight: '600',
        color: '#6c757d',
        marginBottom: '20px',
        paddingBottom: '10px',
        borderBottom: '2px solid #6c757d'
    },
    modelsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
        gap: '20px',
        justifyContent: 'center'
    },
    modelCard: {
        backgroundColor: '#fff',
        borderRadius: '10px',
        padding: '15px',
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        border: '2px solid #6c757d',
        position: 'relative',
        '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
        }
    },
    modelImage: {
        width: '120px',
        height: '100px',
        objectFit: 'contain',
        marginBottom: '10px'
    },
    modelName: {
        fontSize: '14px',
        fontWeight: '500',
        color: '#6c757d',
        margin: '10px 0 5px'
    },
    repairIcon: {
        position: 'absolute',
        top: '10px',
        right: '10px',
        backgroundColor: '#6c757d',
        color: '#fff',
        borderRadius: '50%',
        width: '30px',
        height: '30px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        opacity: '0.9'
    },
    noModelsText: {
        textAlign: 'center',
        color: '#6c757d',
        fontSize: '18px',
        gridColumn: '1 / -1',
        padding: '40px'
    }
};

// Add keyframes for spinner animation
const styleSheet = document.createElement("style");
styleSheet.textContent = `
    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
`;
document.head.appendChild(styleSheet);

export default ConsoleModels;